// src/hooks/useAuthPreflight.ts
import { useMemo } from 'react';
import { useSession } from '@/lib/session/useSession';

/**
 * useAuthPreflight — лёгкий предгейт, завязанный только на useSession.
 * Никаких сетевых вызовов. Возвращает компактный статус доступа.
 */

export type AccessStatus =
  | { kind: 'loading' }
  | { kind: 'no-session' }
  | { kind: 'forbidden'; missing: string[] }
  | { kind: 'ok'; userId: string; roles: string[] };

export type PreflightOptions = {
  /** Любая из этих ролей подойдёт (OR). Если пусто — доступ без ролей. */
  anyOfRoles?: string[];
};

export function useAuthPreflight(opts?: PreflightOptions): AccessStatus {
  const { session, loading } = useSession();
  const need = opts?.anyOfRoles?.filter(Boolean) ?? [];

  return useMemo<AccessStatus>(() => {
    if (loading) return { kind: 'loading' };
    if (!session) return { kind: 'no-session' };
    if (need.length > 0) {
      const hasAny = session.roles.some((r) => need.includes(r));
      if (!hasAny) {
        const missing = need.filter((r) => !session.roles.includes(r));
        return { kind: 'forbidden', missing };
      }
    }
    return { kind: 'ok', userId: session.userId, roles: session.roles };
  }, [loading, session, need]);
}